import { useState, useEffect } from 'react';
import { X, Cookie } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

export const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const { language } = useLanguage();

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Small delay for better UX
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    setIsVisible(false);
  };

  const handleDecline = () => {
    localStorage.setItem('cookieConsent', 'declined');
    setIsVisible(false);
  };

  if (!isVisible) return null;

  const content = {
    en: {
      title: 'Cookie Notice',
      message: 'We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. By clicking "Accept All", you consent to our use of cookies.',
      acceptAll: 'Accept All',
      decline: 'Decline',
      learnMore: 'Learn More'
    },
    tr: {
      title: 'Çerez Bildirimi',
      message: 'Tarama deneyiminizi geliştirmek, site trafiğini analiz etmek ve içeriği kişiselleştirmek için çerezler kullanıyoruz. "Tümünü Kabul Et" seçeneğine tıklayarak çerez kullanımımızı kabul etmiş olursunuz.',
      acceptAll: 'Tümünü Kabul Et',
      decline: 'Reddet',
      learnMore: 'Daha Fazla Bilgi'
    },
    ru: {
      title: 'Уведомление о файлах cookie',
      message: 'Мы используем файлы cookie для улучшения вашего опыта просмотра, анализа трафика сайта и персонализации контента. Нажимая "Принять все", вы соглашаетесь на использование нами файлов cookie.',
      acceptAll: 'Принять все',
      decline: 'Отклонить',
      learnMore: 'Узнать больше'
    }
  };

  // Default to Turkish if language not found
  const t = content[language] || content.tr;

  return (
    <div 
      data-testid="cookie-consent-banner"
      className="fixed bottom-0 left-0 right-0 z-[100] animate-slide-up"
    >
      <div className="bg-navy/95 backdrop-blur-md border-t border-white/10 shadow-2xl">
        <div className="container-bespoke py-4 md:py-6">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            {/* Icon & Text */}
            <div className="flex items-start gap-4 flex-1">
              <div className="p-2 bg-signal/20 rounded-lg shrink-0">
                <Cookie className="w-6 h-6 text-signal" />
              </div>
              <div>
                <h3 className="text-white font-heading font-bold text-lg mb-1">
                  {t.title}
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed max-w-2xl">
                  {t.message}
                </p>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex items-center gap-3 w-full md:w-auto">
              <button
                data-testid="cookie-decline-btn"
                onClick={handleDecline}
                className="flex-1 md:flex-none px-5 py-2.5 text-sm font-medium text-slate-400 hover:text-white border border-slate-600 hover:border-slate-400 rounded-lg transition-all duration-300"
              >
                {t.decline}
              </button>
              <button
                data-testid="cookie-accept-btn"
                onClick={handleAccept}
                className="flex-1 md:flex-none px-6 py-2.5 text-sm font-medium text-white bg-signal hover:bg-signal/90 rounded-lg transition-all duration-300 shadow-lg shadow-signal/25"
              >
                {t.acceptAll}
              </button>
            </div>

            {/* Close button for mobile */}
            <button
              data-testid="cookie-close-btn"
              onClick={handleDecline}
              className="absolute top-3 right-3 md:hidden p-1 text-slate-500 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
